import { ColumnDef } from "@tanstack/react-table";
import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { DescriptionOfWarehouseItem } from "../../../constants/common";
import { Button } from "../../button";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";

interface DescriptionOfWarehouseItemsTableProps {
  addedDescriptionOfWarehouseItems: DescriptionOfWarehouseItem[];
  setAddedDescriptionOfWarehouseItems: Dispatch<
    SetStateAction<DescriptionOfWarehouseItem[]>
  >;
  setEditingItem: React.Dispatch<
    React.SetStateAction<DescriptionOfWarehouseItem | null>
  >;
  setIsAddItemFormVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onFormReset?: () => void;
  isFieldsDisabled: boolean;
}

const DescriptionOfWarehouseItemsTable = ({
  addedDescriptionOfWarehouseItems,
  setAddedDescriptionOfWarehouseItems,
  setEditingItem,
  setIsAddItemFormVisible,
  onFormReset,
  isFieldsDisabled,
}: DescriptionOfWarehouseItemsTableProps) => {
  const { t } = useTranslation();

  const handleEdit = useCallback(
    (worker: DescriptionOfWarehouseItem) => {
      setEditingItem(worker);
      setIsAddItemFormVisible(true);
    },
    [setEditingItem, setIsAddItemFormVisible]
  );

  const handleDelete = useCallback(
    (id: string) => {
      setAddedDescriptionOfWarehouseItems((prev) =>
        prev.filter((visitor) => visitor.id !== id)
      );
      setEditingItem(null);

      // Reset the form when a visitor is deleted
      if (onFormReset) {
        onFormReset();
      }
    },
    [onFormReset, setAddedDescriptionOfWarehouseItems, setEditingItem]
  );

  const columns: ColumnDef<DescriptionOfWarehouseItem>[] = useMemo(
    () => [
      {
        accessorKey: "itemType",
        header: t("itemType"),
      },
      {
        accessorKey: "quantity",
        header: t("quantity"),
      },

      {
        accessorKey: "supplyingExportingEntityForPermitHolder",
        header: t("supplyingExportingEntityForPermitHolder"),
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <div className="flex gap-1 items-center">
              <Button
                type="button"
                onClick={() => handleEdit(row.original)}
                className="py-0.5"
              >
                {isFieldsDisabled ? t("view") : t("edit")}
              </Button>
              {!isFieldsDisabled && (
                <Button
                  onClick={() => handleDelete(row.original.id)}
                  className="bg-red-600 hover:bg-red-500 py-0.5"
                  disabled={isFieldsDisabled}
                >
                  {t("delete")}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [t, isFieldsDisabled, handleEdit, handleDelete]
  );

  return (
    <TableWrapper
      data={addedDescriptionOfWarehouseItems}
      columns={columns}
      isPagination={false}
      isColumnChooser={false}
    />
  );
};

export default React.memo(DescriptionOfWarehouseItemsTable);
